import React, { useRef } from 'react';

export type LotDetailTabKey = 'general' | 'analytics' | 'lots' | 'documents' | 'discussion' | 'protocols' | 'contracts' | 'appeal';

interface LotDetailTabsProps {
  id: string;
  label: string;
  items: { key: LotDetailTabKey; label: string; icon: React.ReactNode }[];
  active: LotDetailTabKey;
  onChange: (key: LotDetailTabKey) => void;
}

/** Every section remains visible; long translated labels wrap within the grid. */
export default function LotDetailTabs({ id, label, items, active, onChange }: LotDetailTabsProps) {
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);
  const navigate = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    let next: number;
    if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = items.length - 1;
    else if (event.key === 'ArrowRight') next = (index + 1) % items.length;
    else if (event.key === 'ArrowLeft') next = (index - 1 + items.length) % items.length;
    else return;
    event.preventDefault();
    onChange(items[next].key);
    buttons.current[next]?.focus();
  };

  return <div role="tablist" aria-label={label}
    className="mb-5 grid grid-cols-2 gap-1.5 rounded-2xl border border-slate-200 bg-slate-100/80 p-1.5 sm:grid-cols-4">
    {items.map((item, index) => <button key={item.key} type="button" role="tab"
      id={`${id}-${item.key}-tab`} aria-controls={`${id}-panel`} aria-selected={active === item.key}
      tabIndex={active === item.key ? 0 : -1} ref={button => { buttons.current[index] = button; }}
      onClick={() => onChange(item.key)} onKeyDown={event => navigate(event, index)}
      className={`inline-flex min-h-[44px] min-w-0 items-center justify-center gap-2 rounded-xl px-2.5 py-2.5 text-center text-[13px] font-semibold leading-5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-goszakup-500 focus-visible:ring-offset-2 sm:px-3 sm:text-sm ${active === item.key
        ? 'bg-goszakup-700 text-white shadow-sm' : 'text-slate-600 hover:bg-white hover:text-slate-900'}`}>
      <span aria-hidden="true" className="hidden shrink-0 min-[360px]:block">{item.icon}</span>
      <span className="min-w-0 [overflow-wrap:anywhere]">{item.label}</span>
    </button>)}
  </div>;
}
